import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Modal, ModalHeader, ModalBody, Form, FormGroup,
    Label, Input, Button, Row, Col, InputGroup,
    Nav, NavItem, NavLink, TabContent, TabPane, Table
} from 'reactstrap';
import Swal from 'sweetalert2';
import Select from 'react-select';
import classNames from 'classnames';

import useTerceiro from '../../context/hooks/useTerceiro';
import useGerenteFotografo from '../../context/hooks/useGerenteFotografo';

import { AuthContext } from '../../context/AuthContext';
import { TerceiroContext } from '../../context/TerceiroContext';
import { TipoTerceiroContext } from '../../context/TipoTerceiro';
import { TipoServicoContext } from '../../context/TipoServicoContext';
import { CreateTerceiroType } from '../../context/hooks/useTerceiro';
import { CurrencyInput } from '../Form/CurrencyInput';

import useUser from '../../context/hooks/useUser';
import CreateTipoTerceiroModal from '../TipoTerceiro/CreateTipoTerceiroModal';

interface Props {
    isOpen: boolean;
    toggle: () => void;
    onCreated?: () => void;
    fixedTipoTerceiroId?: number;
}

type ServicoSelecionado = { tipoServicoId: number; nome: string; value: number };

export const CreateTerceiroModal: React.FC<Props> = ({
    isOpen,
    toggle,
    onCreated,
    fixedTipoTerceiroId
}) => {
    const { objUserTk } = useContext(AuthContext);
    const { createTerceiro } = useContext(TerceiroContext);
    const { listTipoTerceiro, getAllTipoTerceiroPaged } = useContext(TipoTerceiroContext);
    const { listTipoServico, getAllTipoServicoPaged } = useContext(TipoServicoContext);
    const { validateCpf } = useUser();

    const { handleGetFotografosByEmpresa, listFotografos } = useTerceiro();
    const { createGerenteFotografo } = useGerenteFotografo();

    const [activeTab, setActiveTab] = useState<'dados' | 'servicos'>('dados');
    const [createTipoTerceiroOpen, setCreateTipoTerceiroOpen] = useState(false);

    const emptyState: CreateTerceiroType = {
        name: '',
        email: '',
        CPF: '',
        telephone: '',
        telephone_01: '',
        address: '',
        addressNumber: '',
        neighborhood: '',
        city: '',
        state: '',
        zipCode: '',
        landline: '',
        bank: '',
        agency: '',
        currentAccount: '',
        observation: '',
        pix: '',
        spendingCeiling: 0,
        isManager: false,
        password: '',
        tipoTerceiroId: fixedTipoTerceiroId ?? 0,
        empresaId: objUserTk.EmpresaId,
        servicos: [],
    };

    const [formData, setFormData] = useState<CreateTerceiroType>(emptyState);

    const [servicosSelecionados, setServicosSelecionados] = useState<ServicoSelecionado[]>([]);

    const [valorModalOpen, setValorModalOpen] = useState(false);
    const [servicoEmEdicao, setServicoEmEdicao] = useState<{ tipoServicoId: number; nome: string } | null>(null);
    const [valorInput, setValorInput] = useState<number>(0);

    const [selectedFotografos, setSelectedFotografos] = useState<number[]>([]);

    const isTipoVendedor = useMemo(
        () => listTipoTerceiro.find(t => t.id === formData.tipoTerceiroId)?.type.toLowerCase() === 'vendedor',
        [listTipoTerceiro, formData.tipoTerceiroId]
    );
    const isTipoFotografo = useMemo(
        () => listTipoTerceiro.find(t => t.id === formData.tipoTerceiroId)?.type.toLowerCase() === 'fotógrafo',
        [listTipoTerceiro, formData.tipoTerceiroId]
    );

    useEffect(() => {
        if (isOpen) {
            setFormData(emptyState);
            setSelectedFotografos([]);
            setServicosSelecionados([]);
            setActiveTab('dados');
            getAllTipoTerceiroPaged(1, 1000, objUserTk.EmpresaId);
            getAllTipoServicoPaged(1, 1000, objUserTk.EmpresaId);
        }
    }, [isOpen, fixedTipoTerceiroId]);

    useEffect(() => {
        if (formData.isManager && isTipoFotografo) {
            handleGetFotografosByEmpresa(objUserTk.EmpresaId);
        }
    }, [formData.isManager, isTipoFotografo]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: name === 'tipoTerceiroId' ? Number(value) : value }));
    };

    const handleValidateCpf = async () => {
        const rawCpf = formData.CPF?.replace(/\D/g, '') ?? '';
        if (!rawCpf || rawCpf.length !== 11) return;

        const result = await validateCpf(rawCpf);
        if (result?.tipo) {
            const confirm = await Swal.fire({
                icon: 'warning',
                title: 'CPF já cadastrado!',
                html: `<b>Tipo:</b> ${result.tipo}<br/><b>Nome:</b> ${result.nome}<br/><br/>Deseja continuar mesmo assim?`,
                showCancelButton: true,
                confirmButtonText: 'Continuar cadastro',
                cancelButtonText: 'Limpar CPF',
                reverseButtons: true
            });

            if (!confirm.isConfirmed) {
                setFormData(prev => ({ ...prev, CPF: '' }));
            }
        }
    };

    const tipoServicoOptions = useMemo(() => {
        const usados = new Set(servicosSelecionados.map(s => s.tipoServicoId));
        return listTipoServico
            .filter(s => !usados.has(s.id))
            .map(s => ({ value: s.id, label: s.name }));
    }, [listTipoServico, servicosSelecionados]);

    const onSelectServico = (opt: any) => {
        if (!opt) return;
        const { value, label } = opt;
        const svc = listTipoServico.find(s => s.id === value);
        const valorPadrao = svc?.value ?? 0;
        setServicoEmEdicao({ tipoServicoId: value, nome: label });
        setValorInput(valorPadrao);
        setValorModalOpen(true);
    };

    const salvarValorServico = () => {
        if (!servicoEmEdicao) return;

        const parsed = Number(valorInput);
        if (!Number.isFinite(parsed) || parsed < 0) {
            Swal.fire('Valor inválido', 'Informe um valor válido para o serviço.', 'warning');
            return;
        }

        setServicosSelecionados(prev => {
            const exists = prev.some(s => s.tipoServicoId === servicoEmEdicao.tipoServicoId);
            if (exists) {
                return prev.map(s =>
                    s.tipoServicoId === servicoEmEdicao.tipoServicoId ? { ...s, value: parsed } : s
                );
            }
            return [
                ...prev,
                { tipoServicoId: servicoEmEdicao.tipoServicoId, nome: servicoEmEdicao.nome, value: parsed }
            ];
        });

        setValorModalOpen(false);
        setServicoEmEdicao(null);
        setValorInput(0);
    };


    const removerServico = (tipoServicoId: number) => {
        setServicosSelecionados(prev => prev.filter(s => s.tipoServicoId !== tipoServicoId));
    };

    const editarValor = (s: ServicoSelecionado) => {
        setServicoEmEdicao({ tipoServicoId: s.tipoServicoId, nome: s.nome });
        setValorInput(s.value);
        setValorModalOpen(true);
    };

    useEffect(() => {
        if (!valorModalOpen && servicoEmEdicao && valorInput !== 0) {
        }
    }, [valorModalOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const servicosPayload = servicosSelecionados.map(s => ({
            tipoServicoId: s.tipoServicoId,
            value: s.value
        }));

        const created = await createTerceiro({
            ...formData,
            servicos: servicosPayload,
        });

        const isGerenteFotografo = isTipoFotografo && formData.isManager && selectedFotografos.length > 0;
        if (created && isGerenteFotografo) {
            await createGerenteFotografo({
                empresaId: objUserTk.EmpresaId,
                gerenteId: (created as any).id,
                fotografoId: selectedFotografos
            });
        }

        if (created) {
            onCreated?.();
            toggle();
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="lg">
                <ModalHeader toggle={toggle}>Adicionar novo terceiro</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleSubmit}>
                        <Nav tabs>
                            <NavItem>
                                <NavLink
                                    className={classNames({ active: activeTab === 'dados' })}
                                    onClick={() => setActiveTab('dados')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Dados
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classNames({ active: activeTab === 'servicos' })}
                                    onClick={() => setActiveTab('servicos')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Serviços
                                </NavLink>
                            </NavItem>
                        </Nav>

                        <TabContent activeTab={activeTab} className="pt-3">
                            <TabPane tabId="dados">
                                <Row>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label className="form-control-label">Tipo de terceiro</Label>
                                            <InputGroup>
                                                <Input
                                                    className="form-control-alternative"
                                                    type="select"
                                                    name="tipoTerceiroId"
                                                    value={formData.tipoTerceiroId}
                                                    onChange={handleChange}
                                                    disabled={!!fixedTipoTerceiroId}
                                                    required
                                                >
                                                    <option value={0}>Selecione um tipo de terceiro</option>
                                                    {listTipoTerceiro.map(t => (
                                                        <option key={t.id} value={t.id}>{t.type}</option>
                                                    ))}
                                                </Input><Button
                                                        style={{ background: '#585858', color: '#fff' }}
                                                        onClick={() => setCreateTipoTerceiroOpen(true)}
                                                    >
                                                        +
                                                    </Button></InputGroup>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {(isTipoVendedor || isTipoFotografo) && (
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label className="form-control-label" htmlFor="spendingCeiling">Teto de Gastos</Label>
                                                <Input
                                                    className="form-control-alternative"
                                                    type="text"
                                                    name="spendingCeiling"
                                                    id="spendingCeiling"
                                                    value={
                                                        new Intl.NumberFormat('pt-BR', {
                                                            style: 'currency',
                                                            currency: 'BRL'
                                                        }).format(Number(formData.spendingCeiling || 0))
                                                    }
                                                    onChange={(e) => {
                                                        const rawValue = e.target.value.replace(/[^\d,]/g, '').replace(',', '.');
                                                        const numeric = parseFloat(rawValue);
                                                        setFormData(prev => ({ ...prev, spendingCeiling: isNaN(numeric) ? 0 : numeric }));
                                                    }}
                                                    placeholder="R$ 0,00"
                                                />
                                            </FormGroup>
                                        </Col>

                                        {isTipoFotografo && (
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label className="form-control-label" htmlFor="isManager">É Gerente?</Label>
                                                    <Input
                                                        className="form-control-alternative"
                                                        type="select"
                                                        name="isManager"
                                                        id="isManager"
                                                        value={formData.isManager ? 'true' : 'false'}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, isManager: e.target.value === 'true' }))}
                                                    >
                                                        <option value="false">Não</option>
                                                        <option value="true">Sim</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        )}
                                    </Row>
                                )}

                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="name">Nome</Label>
                                            <Input
                                                className="form-control-alternative"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Nome"
                                                required
                                            />
                                        </FormGroup>
                                    </Col>

                                    <Col md="6">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="email">Email</Label>
                                            <Input
                                                className="form-control-alternative"
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="Email"
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="CPF">CPF</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="CPF"
                                                id="CPF"
                                                value={formData.CPF}
                                                placeholder="CPF"
                                                onChange={handleChange}
                                                onBlur={handleValidateCpf}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="telephone">Telefone</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="telephone"
                                                id="telephone"
                                                value={formData.telephone}
                                                placeholder="Telefone"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="telephone_01">Telefone 01</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="telephone_01"
                                                id="telephone_01"
                                                value={formData.telephone_01}
                                                placeholder="Telefone 01"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="address">Endereço</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="address"
                                                id="address"
                                                value={formData.address}
                                                placeholder="Endereço"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="addressNumber">Número</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="addressNumber"
                                                id="addressNumber"
                                                value={formData.addressNumber}
                                                placeholder="Número"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="neighborhood">Bairro</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="neighborhood"
                                                id="neighborhood"
                                                value={formData.neighborhood}
                                                placeholder="Bairro"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="city">Cidade</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="city"
                                                id="city"
                                                value={formData.city}
                                                placeholder="Cidade"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="state">Estado</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="state"
                                                id="state"
                                                value={formData.state}
                                                placeholder="Estado"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="zipCode">CEP</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="zipCode"
                                                id="zipCode"
                                                value={formData.zipCode}
                                                placeholder="CEP"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="landline">Telefone Fixo</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="landline"
                                                id="landline"
                                                value={formData.landline}
                                                placeholder="Telefone Fixo"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="observation">Observação</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="textarea"
                                                name="observation"
                                                id="observation"
                                                value={formData.observation}
                                                placeholder="Observação"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="bank">Banco</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="bank"
                                                id="bank"
                                                value={formData.bank}
                                                placeholder="Banco"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="agency">Agência</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="agency"
                                                id="agency"
                                                value={formData.agency}
                                                placeholder="Agência"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="currentAccount">Conta Corrente</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="currentAccount"
                                                id="currentAccount"
                                                value={formData.currentAccount}
                                                placeholder="Conta Corrente"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="pix">Pix</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="text"
                                                name="pix"
                                                id="pix"
                                                value={formData.pix}
                                                placeholder="Pix"
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end mt-3">
                                    <Button style={{ background: '#585858', color: '#fff' }} type="submit">
                                        Criar terceiro
                                    </Button>
                                    <Button
                                        className="ml-2"
                                        style={{ background: '#d9534f', color: '#fff' }}
                                        onClick={toggle}
                                        type="button"
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            </TabPane>

                            <TabPane tabId="servicos">
                                <Row>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label className="form-control-label">Selecionar serviço</Label>
                                            <Select
                                                className="form-control-alternative"
                                                options={tipoServicoOptions}
                                                onChange={onSelectServico}
                                                value={null}
                                                placeholder={listTipoServico.length ? 'Digite para buscar...' : 'Nenhum serviço cadastrado para esta empresa'}
                                                classNamePrefix="select"
                                                isDisabled={!listTipoServico.length}
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="12">
                                        <Table bordered responsive>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '60%' }}>Serviço</th>
                                                    <th style={{ width: '20%' }}>Valor</th>
                                                    <th style={{ width: '20%' }}>Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {servicosSelecionados.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={3} className="text-center text-muted">
                                                            Nenhum serviço adicionado.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    servicosSelecionados.map(s => (
                                                        <tr key={s.tipoServicoId}>
                                                            <td>{s.nome}</td>
                                                            <td>
                                                                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(s.value)}
                                                            </td>
                                                            <td>
                                                                <Button size="sm" color="secondary" onClick={() => editarValor(s)}>
                                                                    Editar valor
                                                                </Button>{' '}
                                                                <Button size="sm" color="danger" onClick={() => removerServico(s.tipoServicoId)}>
                                                                    Remover
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end mt-3">
                                    <Button style={{ background: '#585858', color: '#fff' }} type="submit">
                                        Criar terceiro
                                    </Button>
                                    <Button
                                        className="ml-2"
                                        style={{ background: '#d9534f', color: '#fff' }}
                                        onClick={toggle}
                                        type="button"
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            </TabPane>
                        </TabContent>
                    </Form>
                </ModalBody>
            </Modal>

            <Modal isOpen={valorModalOpen} toggle={() => setValorModalOpen(false)} backdrop="static">
                <ModalHeader toggle={() => setValorModalOpen(false)}>
                    {servicoEmEdicao ? `Definir valor: ${servicoEmEdicao.nome}` : 'Definir valor'}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label className="form-control-label">Valor (R$)</Label>
                        <CurrencyInput
                            name="value"
                            value={valorInput}
                            onValueChange={(v) => setValorInput(v ?? 0)}
                            placeholder="R$ 0,00"
                        />
                    </FormGroup>
                    <div className="d-flex justify-content-end">
                        <Button color="secondary" onClick={() => setValorModalOpen(false)} type="button">
                            Cancelar
                        </Button>
                        <Button className="ml-2" style={{ background: '#585858', color: '#fff' }} onClick={salvarValorServico} type="button">
                            Salvar
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

            <CreateTipoTerceiroModal
                isOpen={createTipoTerceiroOpen}
                toggle={() => setCreateTipoTerceiroOpen(false)}
                onCreated={() => getAllTipoTerceiroPaged(1, 9, objUserTk.EmpresaId)}
            />
        </>
    );
};

export default CreateTerceiroModal;
